import type {
  SelectChangeEvent
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Card,
  Table,
  Stack,
  Alert,
  Button,
  Select,
  MenuItem,
  TableBody,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  TableContainer,
  InputAdornment,
  TablePagination,
  CircularProgress
} from '@mui/material';

import { EmployeeStatus, type EmployeeDto, AllUsers } from 'src/api/api_data';
import { usersApi } from 'src/api/users.api';
import { getUserIdFromToken } from 'src/utils/jwt';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { emptyRows } from 'src/sections/employee/utils';
import { TableNoData } from 'src/sections/employee/table-no-data';
import { TableEmptyRows } from 'src/sections/employee/table-empty-rows';
import { EmployeeTableHead } from 'src/sections/employee/employee-table-head';

import BranchEmployeeTableRow from './BranchEmployeeTableRow';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'İsim', width: 200 },
  { id: 'email', label: 'Email', width: 220 },
  { id: 'position', label: 'Pozisyon', width: 160 },
  { id: 'status', label: 'Durum', width: 120 },
  { id: 'isVerified', label: 'Doğrulandı', width: 100, align: 'center' as const, sortable: false },
  { id: 'actions', label: 'Aksiyonlar', width: 80, align: 'right' as const }
];

interface BranchEmployeesTabProps {
  branchId: string;
  branchName: string;
}

export default function BranchEmployeesTab({ branchId, branchName }: BranchEmployeesTabProps) {
  const navigate = useNavigate();

  // State management
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<EmployeeStatus | 'AWAITING_APPROVAL' | ''>('');
  const [selected, setSelected] = useState<string[]>([]);

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = getUserIdFromToken();
      if (!userId) {
        throw new Error('Kullanıcı bilgisi bulunamadı');
      }
      
      try {
        // Yeni backend API'yi dene
        const response = await usersApi.getEmployeesForBranch(branchId, page, rowsPerPage);
        if (response.content) {
          // Backend'den gelen EmployeeDto'yu EmployeeDto'ya map et
          const mappedEmployees: EmployeeDto[] = response.content.map((user: any) => ({
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            position: user.position || 'EMPLOYEE',
            status: (user.status as EmployeeStatus) || EmployeeStatus.INACTIVE,
            branchId: user.branchId || branchId,
            branchName, // Mevcut şube adını kullan
            verified: user.verified || false,
            deleted: user.deleted || false,
            deviceId: user.deviceId || null,
            key: user.key || null,
            lastCheckInTime: user.lastCheckInTime || null,
            totalWorkHours: user.totalWorkHours || 0,
            attendanceCount: user.attendanceCount || 0,
            awaitingDeviceApproval: user.awaitingDeviceApproval || false
          }));
          
          setEmployees(mappedEmployees);
          setTotalElements(response.totalElements);
        } else {
          setEmployees([]);
          setTotalElements(0);
        }
      } catch (apiError) {
        
        // Fallback: Eski API'yi kullan
        const response = await AllUsers(userId, page, rowsPerPage);
        
        // AllUsers fonksiyonu direkt array döndürüyor, PageResponse formatına çevir
        const employeeData = Array.isArray(response) ? response : [];
        
        // Şube ID'sine göre filtrele (branchName alanından)
        const branchEmployees = employeeData.filter((emp: EmployeeDto) => 
          emp.branchName && emp.branchName.toLowerCase().includes(branchName.toLowerCase())
        );
        
        setEmployees(branchEmployees);
        setTotalElements(branchEmployees.length);
      }
    } catch (err: any) {
      setError(err.message || 'Çalışanlar yüklenirken bir hata oluştu');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, branchName, branchId]);

  // Load employees
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Filter employees based on search and status
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = searchTerm === '' || 
      `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.position.toLowerCase().includes(searchTerm.toLowerCase());
    
    let matchesStatus = true;
    if (statusFilter === 'AWAITING_APPROVAL') {
      // BranchEmployeeTableRow ile ayni tanim: backend'in hesapladigi awaitingDeviceApproval
      // alani ilk cihaz onayi bekleyenleri isaretler. Alan eski yanitlarda yoksa false sayilir.
      matchesStatus = Boolean(employee.awaitingDeviceApproval);
    } else if (statusFilter !== '') {
      matchesStatus = employee.status === statusFilter;
    }
    
    return matchesSearch && matchesStatus;
  });

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle status filter
  const handleStatusFilter = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value as EmployeeStatus | 'AWAITING_APPROVAL' | '');
  };

  // Handle selection
  const handleSelectAllRows = (checked: boolean) => {
    if (checked) {
      const newSelected = filteredEmployees.map(employee => employee.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
        <Button 
          variant="outlined" 
          size="small" 
          onClick={fetchEmployees}
          sx={{ ml: 2 }}
        >
          Tekrar Dene
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        alignItems={{ xs: 'flex-start', sm: 'center' }} 
        justifyContent="space-between" 
        spacing={2}
        mb={3}
      >
        <Typography variant="h6">
          {branchName} Şubesi Çalışanları ({totalElements})
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={() => navigate('/dashboard/employee/create')}
          sx={{ 
            minWidth: { xs: '100%', sm: 'auto' }
          }}
        >
          Yeni Çalışan
        </Button>
      </Stack>

      {/* Search and Filter */}
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        spacing={2} 
        sx={{ mb: 3 }}
      >
        <TextField
          fullWidth
          placeholder="Çalışan ara..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" />
              </InputAdornment>
            )
          }}
        />
        
        <FormControl sx={{ minWidth: { xs: '100%', sm: 150 } }}>
          <InputLabel>Durum</InputLabel>
          <Select
            value={statusFilter}
            label="Durum"
            onChange={handleStatusFilter}
          >
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value={EmployeeStatus.ACTIVE}>Aktif</MenuItem>
            <MenuItem value="AWAITING_APPROVAL">Onay Bekliyor</MenuItem>
            <MenuItem value={EmployeeStatus.INACTIVE}>Pasif</MenuItem>
            <MenuItem value={EmployeeStatus.ON_LEAVE}>İzinli</MenuItem>
            <MenuItem value={EmployeeStatus.BANNED}>Yasaklı</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      <Card>
        <Scrollbar>
          <TableContainer sx={{ overflow: 'unset', maxWidth: '100%' }}>
            <Table sx={{ minWidth: { xs: 600, sm: 800 } }}>
                             <EmployeeTableHead
                 order="asc"
                 orderBy="name"
                 rowCount={filteredEmployees.length}
                 numSelected={selected.length}
                 onSort={() => {}}
                 onSelectAllRows={handleSelectAllRows}
                 headLabel={TABLE_HEAD}
               />
                             <TableBody>
                 {filteredEmployees.map((employee) => (
                   <BranchEmployeeTableRow 
                     key={employee.id} 
                     employee={employee}
                     selected={isSelected(employee.id)}
                     onSelectRow={() => handleClick(employee.id)}
                     onStatusChange={fetchEmployees}
                   />
                 ))}
                <TableEmptyRows
                  height={77}
                  emptyRows={emptyRows(page, rowsPerPage, filteredEmployees.length)}
                />
                {filteredEmployees.length === 0 && (
                  <TableNoData searchQuery={searchTerm} />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          component="div"
          page={page}
          count={totalElements}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[5, 10, 25]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Sayfa başına satır:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} arası, toplam ${count !== -1 ? count : `${to}'dan fazla`}`
          }
        />
      </Card>
    </Box>
  );
}